@if (isLoading) {
  <div class="profile-loading">
    Cargando información completa de tu expediente...
  </div>
} @else if (volunteer) {
  <div class="profile-page">

    <!-- FEEDBACK TOAST / ALERTAS -->
    @if (feedbackMessage) {
      <div class="feedback-toast toast-success">
        <span>✓</span> {{ feedbackMessage }}
      </div>
    }
    @if (feedbackError) {
      <div class="feedback-toast toast-error">
        <span>✕</span> {{ feedbackError }}
      </div>
    }

    <!-- =========================================================
         1. HERO DE PERFIL (CON FOLIO COPIABLE Y METADATOS - CERO HORAS)
         ========================================================= -->
    <section class="volunteer-hero">
      <div class="hero-top-row">
        <div class="hero-main">
          <div class="hero-eyebrow-container">
            <span class="hero-eyebrow">
              MI EXPEDIENTE DE VOLUNTARIO
            </span>
            <span class="hero-type-pill">
              VOLUNTARIO GENERAL
            </span>

            @if (volunteer.accountStatus === 'pending') {
              <span class="status-pill pill-pending">⏳ Pendiente de Aceptación</span>
            } @else if (volunteer.accountStatus === 'rejected') {
              <span class="status-pill pill-rejected">✕ Rechazado</span>
            } @else if (volunteer.accountStatus === 'inactive') {
              <span class="status-pill pill-rejected">● Inactivo</span>
            } @else {
              <span class="status-pill pill-active">● Activo</span>
            }
          </div>

          <div class="hero-title-row">
            <h1 class="hero-name">
              {{ volunteer.fullName || volunteer.mail || 'Voluntario General' }}
            </h1>

            @if (volunteer.folio) {
              <div
                class="hero-folio-badge"
                role="button"
                tabindex="0"
                (click)="copyFolio(volunteer.folio)"
                (keydown.enter)="copyFolio(volunteer.folio)"
                (keydown.space)="copyFolio(volunteer.folio); $event.preventDefault()"
                [title]="copiedFolio() ? '¡Folio copiado!' : 'Haz clic para copiar el folio institucional'"
              >
                <span class="folio-tag">FOLIO:</span>
                <span class="folio-code">{{ volunteer.folio }}</span>
                @if (copiedFolio()) {
                  <span class="folio-copied-toast">¡Copiado!</span>
                }
              </div>
            }
          </div>

          <p class="hero-subtitle">
            {{ volunteer.mail || 'Sin correo registrado' }}
            <span class="hero-meta-dot">•</span>
            {{ volunteer.phone || 'Sin teléfono' }}
          </p>
        </div>
      </div>

      <!-- FILA DE MÉTRICAS / INFORMACIÓN INTEGRADA (CERO HORAS) -->
      <div class="hero-stats-row">
        <div class="hero-stat-card">
          <span class="hero-stat-label">ESTATUS</span>
          <div class="hero-stat-value">
            @if (volunteer.accountStatus === 'pending') {
              <strong style="color: #fde047;">⏳ Pendiente</strong>
            } @else if (volunteer.accountStatus === 'rejected') {
              <strong style="color: #fca5a5;">✕ Rechazado</strong>
            } @else if (volunteer.accountStatus === 'inactive') {
              <strong style="color: #fca5a5;">● Inactivo</strong>
            } @else {
              <strong style="color: #86efac;">● Activo</strong>
            }
          </div>
          <small class="hero-stat-sub">Estado actual de acceso</small>
        </div>

        <div class="hero-stat-card hero-stat-card-combined">
          <div class="combined-stat-col">
            <span class="hero-stat-label">EMISIÓN EXPEDIENTE</span>
            <strong class="hero-stat-value">
              {{ volunteer.fechaEmisionExpediente ? (volunteer.fechaEmisionExpediente | date:'dd/MM/yyyy HH:mm') : 'N/A' }}
            </strong>
            <small class="hero-stat-sub">Envío de formulario</small>
          </div>

          <div class="combined-stat-divider"></div>

          <div class="combined-stat-col">
            <span class="hero-stat-label">ACEPTACIÓN / EXPEDICIÓN</span>
            <strong class="hero-stat-value" [style.color]="volunteer.accountStatus === 'active' ? '#86efac' : 'rgba(255, 255, 255, 0.9)'">
              {{ (volunteer.fechaAceptacionExpediente || volunteer.fechaExpedicionExpediente) ? ((volunteer.fechaAceptacionExpediente || volunteer.fechaExpedicionExpediente) | date:'dd/MM/yyyy HH:mm') : (volunteer.accountStatus === 'pending' ? 'Pendiente' : 'N/A') }}
            </strong>
            <small class="hero-stat-sub">Validación oficial ABAQ</small>
          </div>
        </div>

        <div class="hero-stat-card">
          <span class="hero-stat-label">OPORTUNIDADES GENERADAS</span>
          <strong class="hero-stat-value">
            {{ opportunitiesCount }}
            <span style="font-size: 0.85rem; font-weight: 600; opacity: 0.85; margin-left: 2px;">
              {{ opportunitiesCount === 1 ? 'oportunidad' : 'oportunidades' }}
            </span>
          </strong>
          <small class="hero-stat-sub">Vinculaciones institucionales</small>
        </div>

        <div class="hero-stat-card">
          <span class="hero-stat-label">ACTIVIDADES ASIGNADAS</span>
          <strong class="hero-stat-value">
            {{ activitiesCount }}
            <span style="font-size: 0.85rem; font-weight: 600; opacity: 0.85; margin-left: 2px;">
              {{ activitiesCount === 1 ? 'actividad' : 'actividades' }}
            </span>
          </strong>
          <small class="hero-stat-sub">Participaciones en programas</small>
        </div>

        <div class="hero-stat-card hero-stat-card-total">
          <span class="hero-stat-label">TOTAL REGISTROS</span>
          <strong class="hero-stat-value">
            {{ totalRecordsCount }}
            <span style="font-size: 0.85rem; font-weight: 600; opacity: 0.85; margin-left: 2px;">
              registros
            </span>
          </strong>
          <small class="hero-stat-sub">Historial consolidado</small>
        </div>
      </div>
    </section>

    <!-- BITÁCORA DE ESTATUS / BAJA -->
    @if (volunteer.accountStatus === 'inactive') {
      <div class="inactive-reason-banner">
        <div>
          <span class="inactive-banner-title">Cuenta dada de baja (inactiva)</span>
          <span class="inactive-banner-reason">
            Causal: {{ getFriendlyReason(lastStatusChange?.reason) }}
          </span>
          @if (lastStatusChange?.comments) {
            <p class="inactive-banner-comments">{{ lastStatusChange.comments }}</p>
          }
          @if (lastStatusChange?.date) {
            <span class="status-history-date">{{ lastStatusChange.date | date:'dd/MM/yyyy HH:mm' }}</span>
          }
        </div>
      </div>
    } @else if (volunteer.accountStatus === 'active') {
      <div class="status-active-card">
        <div class="status-active-header">
          <div class="status-active-badge">
            <span class="status-badge-dot"></span>
            <span>Cuenta Activa y Verificada</span>
          </div>
          <span class="status-active-date">
            {{ (lastStatusChange?.date || volunteer.fechaEmisionExpediente) ? ((lastStatusChange?.date || volunteer.fechaEmisionExpediente) | date:'dd/MM/yyyy HH:mm') : 'Activo' }}
          </span>
        </div>
        <div class="status-active-body">
          <p class="status-active-message">
            Tu expediente y registro han sido formalmente aprobados por la Coordinación de ABAQ. Tienes acceso completo a todas las convocatorias y registro de actividades de voluntariado.
          </p>
          @if (lastStatusChange?.comments) {
            <div class="status-active-comments">
              <span class="material-symbols-outlined comment-icon">chat_bubble_outline</span>
              <div>
                <strong>Mensaje de Coordinación:</strong>
                <p>{{ lastStatusChange.comments }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- =========================================================
         2. INFORMACIÓN PERSONAL Y CONTACTO (EDITABLE: TELÉFONO)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">PERFIL</span>
          <h2>Información del voluntario</h2>
          <p>Datos personales y de contacto registrados en tu expediente institucional.</p>
        </div>

        <div class="edit-btn-group">
          @if (!isEditingPersonal) {
            <button
              type="button"
              class="btn-section-edit"
              (click)="startEditPersonal()"
              title="Modificar datos de contacto"
            >
              Modificar datos
            </button>
          } @else {
            <button
              type="button"
              class="btn-section-cancel"
              (click)="cancelEditPersonal()"
              [disabled]="isSaving"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn-section-save"
              (click)="savePersonal()"
              [disabled]="isSaving"
            >
              @if (isSaving) { Guardando... } @else { ✓ Guardar cambios }
            </button>
          }
        </div>
      </div>

      <div class="info-grid">
        <article class="info-card">
          <span class="info-card-span">FOLIO DE EXPEDIENTE</span>
          <span class="info-card-value value-monospace">
            {{ volunteer.folio || '—' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">NOMBRE COMPLETO</span>
          <span class="info-card-value">
            {{ volunteer.fullName }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">CORREO ELECTRÓNICO</span>
          <span class="info-card-value">
            {{ volunteer.mail || 'No registrado' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">
            TELÉFONO
            @if (isEditingPersonal) {
              <span class="badge-editable-indicator">Editable</span>
            }
          </span>
          @if (isEditingPersonal) {
            <input
              type="tel"
              class="input-editable-active"
              [(ngModel)]="volunteer.phone"
              placeholder="Ej. 4421234567"
            />
          } @else {
            <span class="info-card-value">
              {{ volunteer.phone || 'No registrado' }}
            </span>
          }
        </article>

        <article class="info-card">
          <span class="info-card-span">¿TIENE WHATSAPP?</span>
          <span class="info-card-value">
            {{ volunteer.hasWhatsapp ? '✓ Sí (' + (volunteer.whatsappNumber || volunteer.phone) + ')' : 'No registrado' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">CURP</span>
          <span class="info-card-value value-monospace">
            {{ volunteer.curp || 'No registrada' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">OCUPACIÓN / PROFESIÓN</span>
          <span class="info-card-value">
            {{ volunteer.occupation || 'No registrada' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">FECHA DE NACIMIENTO</span>
          <span class="info-card-value">
            {{ volunteer.birthDate ? (volunteer.birthDate | date:'dd/MM/yyyy') : 'No registrada' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">FECHA DE INICIO DE VOLUNTARIADO</span>
          <span class="info-card-value">
            {{ volunteer.volunteerStartDate ? (volunteer.volunteerStartDate | date:'dd/MM/yyyy') : 'No registrada' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">¿CÓMO TE ENTERASTE DE ABAQ?</span>
          <span class="info-card-value">
            {{ volunteer.howDidYouHearAboutAbaq || 'No especificado' }}
          </span>
        </article>

        <article class="info-card">
          <span class="info-card-span">CONTACTO SECUNDARIO</span>
          <span class="info-card-value">
            {{ volunteer.secondaryPhone || 'Sin contacto secundario' }}
          </span>
        </article>
      </div>
    </section>

    <!-- =========================================================
         3. FORMACIÓN ACADÉMICA (GRADOS DE ESTUDIO - SOLO LECTURA)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">FORMACIÓN ACADÉMICA</span>
          <h2>Historial académico y nivel de estudios</h2>
          <p>Grados e instituciones registradas en tu expediente oficial.</p>
        </div>
      </div>

      @if (volunteer.degrees && volunteer.degrees.length > 0) {
        <div class="sd-combined-table-wrapper">
          <table class="sd-combined-table">
            <thead>
              <tr>
                <th>Nivel</th>
                <th>Institución</th>
                <th>Título / Carrera</th>
                <th>Estatus</th>
              </tr>
            </thead>
            <tbody>
              @for (deg of volunteer.degrees; track (deg.level || '') + '-' + (deg.institution || '') + '-' + $index) {
                <tr>
                  <td><strong>{{ deg.level }}</strong></td>
                  <td>{{ deg.institution }}</td>
                  <td>{{ deg.title || '—' }}</td>
                  <td>
                    <span class="performance-pill perf-good">
                      {{ deg.status || 'Completado' }}
                    </span>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      } @else {
        <div class="empty-state">
          <strong>No hay grados académicos adicionales registrados.</strong>
          <span>Tu nivel de estudios principal se refleja en los datos generales.</span>
        </div>
      }
    </section>

    <!-- =========================================================
         4. EXPEDIENTE DIGITAL EN LA NUBE (SOLO LECTURA - ENLACE PÚBLICO DIRECTO)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">EXPEDIENTE DIGITAL</span>
          <h2>Documentos del expediente en la nube</h2>
          <p>Enlaces públicos registrados durante el alta institucional. Ábrelos en una nueva pestaña.</p>
        </div>
      </div>

      <div class="cloud-docs-grid">
        <article class="cloud-doc-card">
          <span class="cloud-doc-label">IDENTIFICACIÓN OFICIAL (INE)</span>
          @if (hasValidDocument(volunteer.cloudUrls?.ine)) {
            <span class="cloud-doc-status">✓ Enlace disponible</span>
            <button
              type="button"
              class="btn-open-doc"
              (click)="openDocument(volunteer.cloudUrls?.ine)"
            >
              Abrir documento público ↗
            </button>
          } @else {
            <span class="doc-pending-text">Documento pendiente de carga</span>
          }
        </article>

        <article class="cloud-doc-card">
          <span class="cloud-doc-label">CURRÍCULUM VITAE (CV) O SEMBLANZA</span>
          @if (hasValidDocument(volunteer.cloudUrls?.cv)) {
            <span class="cloud-doc-status">✓ Enlace disponible</span>
            <button
              type="button"
              class="btn-open-doc"
              (click)="openDocument(volunteer.cloudUrls?.cv)"
            >
              Abrir documento público ↗
            </button>
          } @else {
            <span class="doc-pending-text">Documento pendiente de carga</span>
          }
        </article>

        <article class="cloud-doc-card">
          <span class="cloud-doc-label">COMPROBANTE DE DOMICILIO O ESTUDIOS</span>
          @if (hasValidDocument(volunteer.cloudUrls?.comprobante)) {
            <span class="cloud-doc-status">✓ Enlace disponible</span>
            <button
              type="button"
              class="btn-open-doc"
              (click)="openDocument(volunteer.cloudUrls?.comprobante)"
            >
              Abrir documento público ↗
            </button>
          } @else {
            <span class="doc-pending-text">Documento pendiente de carga</span>
          }
        </article>
      </div>
    </section>

    <!-- =========================================================
         5. CONSTANCIA OFICIAL DE ACEPTACIÓN (HMAC-SHA256 - SOLO LECTURA)
         ========================================================= -->
    @if (volunteer.accountStatus === 'active') {
      <section class="profile-card">
        <div class="section-header-row">
          <div>
            <span class="section-eyebrow">DOCUMENTO OFICIAL</span>
            <h2>Constancia oficial de aceptación</h2>
            <p>Constancia oficial emitida por ABAQ con sello criptográfico HMAC-SHA256.</p>
          </div>
        </div>

        <div class="official-certs-container">
          <div class="official-cert-card">
            <div class="cert-left">
              <div class="cert-icon">📄</div>
              <div class="cert-info">
                <div class="cert-title">Constancia de Aceptación Oficial</div>
                <div class="cert-desc">
                  Emitida para {{ volunteer.fullName }} con folio {{ volunteer.folio || 'N/A' }}.
                </div>
                <div class="cert-meta">
                  Sello criptográfico HMAC-SHA256 · ABAQ Querétaro
                </div>
              </div>
            </div>

            <button
              type="button"
              class="btn-view-cert"
              (click)="openAcceptanceCertificate()"
            >
              Ver constancia oficial ↗
            </button>
          </div>
        </div>
      </section>
    }

    <!-- =========================================================
         6. SEMBLANZA Y VOCACIÓN (EDITABLE: SEMBLANZA / BIO)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">VOCACIÓN Y PERFIL</span>
          <h2>Semblanza, intereses y talentos</h2>
          <p>Perfil vocacional y afinidad al bienestar animal del voluntario.</p>
        </div>

        <div class="edit-btn-group">
          @if (!isEditingBio) {
            <button
              type="button"
              class="btn-section-edit"
              (click)="startEditBio()"
              title="Modificar semblanza personal"
            >
              Modificar datos
            </button>
          } @else {
            <button
              type="button"
              class="btn-section-cancel"
              (click)="cancelEditBio()"
              [disabled]="isSaving"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn-section-save"
              (click)="saveBio()"
              [disabled]="isSaving"
            >
              @if (isSaving) { Guardando... } @else { ✓ Guardar cambios }
            </button>
          }
        </div>
      </div>

      <div class="bio-section-body">
        <div class="bio-card-container">
          <span class="bio-card-label">
            SEMBLANZA / BIO PERSONAL
            @if (isEditingBio) {
              <span class="badge-editable-indicator">Editable</span>
            }
          </span>
          @if (isEditingBio) {
            <textarea
              class="bio-textarea"
              rows="4"
              [(ngModel)]="volunteer.bioPersonal"
              placeholder="Comparte una breve semblanza personal o experiencia..."
            ></textarea>
          } @else {
            <p class="bio-display-text">
              {{ volunteer.bioPersonal || 'Sin semblanza registrada.' }}
            </p>
          }
        </div>

        <div class="vocational-grid">
          <div class="vocational-card">
            <span class="vocational-card-label">INTERESES</span>
            <p class="vocational-text">
              {{ volunteer.intereses || 'No especificados' }}
            </p>
          </div>

          <div class="vocational-card">
            <span class="vocational-card-label">TALENTOS Y HABILIDADES</span>
            <p class="vocational-text">
              {{ volunteer.talentos || 'No especificados' }}
            </p>
          </div>

          <div class="vocational-card">
            <span class="vocational-card-label">APORTES ESPERADOS A ABAQ</span>
            <p class="vocational-text">
              {{ volunteer.aportesAbaq || 'No especificados' }}
            </p>
          </div>

          <div class="vocational-card">
            <span class="vocational-card-label">EXPERIENCIA CON ANIMALES</span>
            <p class="vocational-text">
              <strong>{{ volunteer.animalExperience ? 'Sí' : 'No' }}</strong>
              @if (volunteer.descExperiencia) {
                — {{ volunteer.descExperiencia }}
              }
            </p>
          </div>

          <div class="vocational-card card-full-width">
            <span class="vocational-card-label">MOTIVACIÓN INSTITUCIONAL</span>
            <p class="vocational-text">
              {{ volunteer.motivacionAbaq || 'No especificada' }}
            </p>
          </div>
        </div>

        @if (volunteer.rankedActivities && volunteer.rankedActivities.length > 0) {
          <div class="vocational-card">
            <span class="vocational-card-label">ACTIVIDADES DE INTERÉS SELECCIONADAS</span>
            <div class="ranked-badges-list">
              @for (act of volunteer.rankedActivities; track $index) {
                <div class="ranked-pill">
                  <span class="rank-num">#{{ $index + 1 }}</span>
                  <span>{{ getActivityTitle(act) }}</span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </section>

    <!-- =========================================================
         7. PROCESO FORMATIVO E INDUCCIÓN (6 MÓDULOS - CERO HORAS)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">PROCESO FORMATIVO</span>
          <h2>Reflexiones de los módulos de inducción</h2>
          <p>Respuestas registradas durante la inducción sobre rescate, valores y misión de ABAQ.</p>
        </div>
      </div>

      @if (hasAnyReflections()) {
        <div class="reflections-accordion">
          @for (mod of reflectionsList; track mod.id) {
            <div class="module-reflection-card">
              <div class="reflection-header">
                {{ mod.name }}
              </div>

              @if (mod.data) {
                <div class="reflection-cards-grid">
                  <div class="reflection-answer-box">
                    <span class="reflection-q-label q-liked">❤️ Lo que más llamó mi atención:</span>
                    <p class="reflection-a-text">{{ mod.data.liked || 'Sin respuesta' }}</p>
                  </div>
                  <div class="reflection-answer-box">
                    <span class="reflection-q-label q-important">⭐ Lo que considero más importante:</span>
                    <p class="reflection-a-text">{{ mod.data.important || 'Sin respuesta' }}</p>
                  </div>
                  <div class="reflection-answer-box">
                    <span class="reflection-q-label q-improve">💡 Sugerencia de mejora / innovación:</span>
                    <p class="reflection-a-text">{{ mod.data.improve || 'Sin respuesta' }}</p>
                  </div>
                </div>
              } @else {
                <div class="empty-state" style="padding: 10px 0; text-align: left;">
                  <span class="no-reflection-note">Módulo completado sin reflexiones adicionales registradas.</span>
                </div>
              }
            </div>
          }
        </div>
      } @else {
        <div class="empty-state">
          <strong>No hay reflexiones de inducción registradas en el expediente.</strong>
          <span>Las respuestas formativas aparecerán aquí al concluir los módulos.</span>
        </div>
      }
    </section>

    <!-- =========================================================
         8. NORMATIVA Y CUMPLIMIENTO (5 CONSENTIMIENTOS - SOLO LECTURA)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">NORMATIVA Y CUMPLIMIENTO</span>
          <h2>Consentimientos y aceptación de términos</h2>
          <p>Condiciones y términos validados durante tu registro. Haz clic en cualquiera para abrir el documento.</p>
        </div>
      </div>

      <div class="consent-grid">
        <a
          [href]="getLegalDocUrl('reglamento')"
          (click)="openLegalDoc('reglamento', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Reglamento General Interno en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ volunteer.consents?.reglamento ? '✅' : '❌' }}</span>
            <span class="consent-title">Reglamento General Interno</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('privacidad')"
          (click)="openLegalDoc('privacidad', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Aviso de Privacidad Integral en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ volunteer.consents?.privacidad ? '✅' : '❌' }}</span>
            <span class="consent-title">Aviso de Privacidad Integral</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('etica')"
          (click)="openLegalDoc('etica', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Código de Ética y Conducta en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ volunteer.consents?.etica ? '✅' : '❌' }}</span>
            <span class="consent-title">Código de Ética y Conducta</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('incognito')"
          (click)="openLegalDoc('incognito', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Permiso de Difusión Institucional en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ volunteer.consents?.incognito ? '✅' : '❌' }}</span>
            <span class="consent-title">Permiso de Difusión Institucional</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>

        <a
          [href]="getLegalDocUrl('consentimiento')"
          (click)="openLegalDoc('consentimiento', $event)"
          target="_blank"
          rel="noopener noreferrer"
          class="consent-doc-link"
          title="Abrir Consentimiento Voluntario Expreso en otra pestaña"
        >
          <div class="consent-left">
            <span class="consent-status-icon">{{ volunteer.consents?.consentimiento ? '✅' : '❌' }}</span>
            <span class="consent-title">Consentimiento Voluntario Expreso</span>
          </div>
          <span class="consent-ext-icon">↗</span>
        </a>
      </div>
    </section>

    <!-- =========================================================
         9. COMUNIDAD Y REDES SOCIALES (EDITABLE)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">COMUNIDAD</span>
          <h2>Redes sociales y canales de contacto</h2>
          <p>Cuentas registradas para comunicación institucional y difusión de actividades.</p>
        </div>

        <div class="edit-btn-group">
          @if (!isEditingSocials) {
            <button
              type="button"
              class="btn-section-edit"
              (click)="startEditSocials()"
              title="Modificar cuentas de redes sociales"
            >
              Modificar datos
            </button>
          } @else {
            <button
              type="button"
              class="btn-section-cancel"
              (click)="cancelEditSocials()"
              [disabled]="isSaving"
            >
              Cancelar
            </button>
            <button
              type="button"
              class="btn-section-save"
              (click)="saveSocials()"
              [disabled]="isSaving"
            >
              @if (isSaving) { Guardando... } @else { ✓ Guardar cambios }
            </button>
          }
        </div>
      </div>

      <div class="social-cards-grid">
        <article class="social-network-card">
          <div class="social-logo fb" title="Facebook">
            <svg class="lucide lucide-facebook" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">FACEBOOK</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="volunteer.socialMedia.facebook"
                placeholder="Usuario de Facebook"
              />
            } @else {
              <span class="social-static-value">
                {{ volunteer.socialMedia.facebook || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo ig" title="Instagram">
            <svg class="lucide lucide-instagram" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
              <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
              <line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">INSTAGRAM</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="volunteer.socialMedia.instagram"
                placeholder="Usuario de Instagram"
              />
            } @else {
              <span class="social-static-value">
                {{ volunteer.socialMedia.instagram || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo tk" title="TikTok">
            <svg class="lucide lucide-tiktok" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">TIKTOK</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="volunteer.socialMedia.tiktok"
                placeholder="Usuario de TikTok"
              />
            } @else {
              <span class="social-static-value">
                {{ volunteer.socialMedia.tiktok || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo x" title="X (Twitter)">
            <svg class="lucide lucide-twitter" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4l11.733 16h4.267l-11.733 -16z"/>
              <path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">X (TWITTER)</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="volunteer.socialMedia.x"
                placeholder="Usuario de X"
              />
            } @else {
              <span class="social-static-value">
                {{ volunteer.socialMedia.x || 'Sin cuenta' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo yt" title="YouTube">
            <svg class="lucide lucide-youtube" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/>
              <polygon points="10 15 15 12 10 9 10 15"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">YOUTUBE</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="volunteer.socialMedia.youtube"
                placeholder="Canal de YouTube"
              />
            } @else {
              <span class="social-static-value">
                {{ volunteer.socialMedia.youtube || 'Sin canal' }}
              </span>
            }
          </div>
        </article>

        <article class="social-network-card">
          <div class="social-logo li" title="LinkedIn">
            <svg class="lucide lucide-linkedin" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
              <rect width="4" height="12" x="2" y="9"/>
              <circle cx="4" cy="4" r="2"/>
            </svg>
          </div>
          <div class="social-info">
            <span class="social-name">LINKEDIN</span>
            @if (isEditingSocials) {
              <input
                type="text"
                class="social-input"
                [(ngModel)]="volunteer.socialMedia.linkedin"
                placeholder="Perfil de LinkedIn"
              />
            } @else {
              <span class="social-static-value">
                {{ volunteer.socialMedia.linkedin || 'Sin perfil' }}
              </span>
            }
          </div>
        </article>
      </div>
    </section>

    <!-- =========================================================
         10. ACREDITACIONES Y ACTIVIDADES (3 PESTAÑAS Y 3 TABLAS EN UN CONTENEDOR - CERO HORAS)
         ========================================================= -->
    <section class="profile-card">
      <div class="section-header-row">
        <div>
          <span class="section-eyebrow">PARTICIPACIÓN</span>
          <h2>Acreditaciones y actividades asignadas</h2>
          <p>Registro institucional de vinculaciones, eventos y comisiones en las que colaboras.</p>
        </div>
      </div>

      <!-- BARRA DE PESTAÑAS -->
      <nav class="sd-tabs" role="tablist" aria-label="Filtro de participación institucional">
        <button
          type="button"
          role="tab"
          class="sd-tab"
          [class.active]="accActiveTab === 'acreditaciones'"
          [attr.aria-selected]="accActiveTab === 'acreditaciones'"
          (click)="setAccTab('acreditaciones')"
        >
          Acreditaciones
          <span class="sd-tab-count">{{ opportunitiesCount }}</span>
        </button>

        <button
          type="button"
          role="tab"
          class="sd-tab"
          [class.active]="accActiveTab === 'actividades'"
          [attr.aria-selected]="accActiveTab === 'actividades'"
          (click)="setAccTab('actividades')"
        >
          Actividades
          <span class="sd-tab-count">{{ activitiesCount }}</span>
        </button>

        <button
          type="button"
          role="tab"
          class="sd-tab"
          [class.active]="accActiveTab === 'todas'"
          [attr.aria-selected]="accActiveTab === 'todas'"
          (click)="setAccTab('todas')"
        >
          Todas
          <span class="sd-tab-count">{{ totalRecordsCount }}</span>
        </button>
      </nav>

      <!-- PESTAÑA 1: TABLA DE ACREDITACIONES (CERO HORAS) -->
      @if (accActiveTab === 'acreditaciones') {
        @if (volunteer.accreditations && volunteer.accreditations.length > 0) {
          <div class="sd-combined-table-wrapper">
            <table class="sd-combined-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Tipo de Acreditación</th>
                  <th>Calificación de Desempeño</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                @for (a of volunteer.accreditations; track (a._id || a.interactionFolio || 'acc') + '-' + $index) {
                  <tr>
                    <td>
                      @if (a.interactionFolio) {
                        <app-folio-pill
                          [folio]="a.interactionFolio"
                          [interactionType]="a.interactionType || 'OP'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td><strong>{{ getAccreditationTypeLabel(a.type) }}</strong></td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(a.status, a.performance)">
                        {{ getPerformanceLabel(a.status, a.performance) }}
                      </span>
                      @if (a.performanceComment || a.comments) {
                        <small class="table-comment-sub">{{ a.performanceComment || a.comments }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="a.status === 'approved' || a.status === 'accredited' ? 'perf-good' : (a.status === 'rejected' ? 'perf-bad' : 'perf-neutral')"
                      >
                        {{ getAccreditationStatusLabel(a.status) }}
                      </span>
                    </td>
                    <td>{{ a.date ? (a.date | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">
            <strong>Aún no tienes acreditaciones registradas.</strong>
            <span>Cuando los coordinadores validen tu colaboración institucional aparecerán en este apartado.</span>
          </div>
        }
      }

      <!-- PESTAÑA 2: TABLA DE ACTIVIDADES ASIGNADAS (CERO HORAS) -->
      @if (accActiveTab === 'actividades') {
        @if (volunteer.assignedActivities && volunteer.assignedActivities.length > 0) {
          <div class="sd-combined-table-wrapper">
            <table class="sd-combined-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Actividad / Categoría</th>
                  <th>Calificación de Desempeño</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                @for (act of volunteer.assignedActivities; track (act.activityRef || act.interactionFolio || 'act') + '-' + $index) {
                  <tr>
                    <td>
                      @if (act.interactionFolio) {
                        <app-folio-pill
                          [folio]="act.interactionFolio"
                          [interactionType]="act.interactionType || 'AC'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td>
                      <strong>{{ act.title || act.type || 'Actividad asignada' }}</strong>
                      @if (act.title && act.type) {
                        <small class="table-comment-sub">{{ act.type }}</small>
                      }
                    </td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(act.status, act.performance)">
                        {{ getPerformanceLabel(act.status, act.performance) }}
                      </span>
                      @if (act.performanceComment) {
                        <small class="table-comment-sub">{{ act.performanceComment }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="act.status === 'validated' ? 'perf-good' : (act.status === 'absent' ? 'perf-absent' : 'perf-neutral')"
                      >
                        {{ getActivityStatusLabel(act.status) }}
                      </span>
                    </td>
                    <td>{{ (act.assignedAt || act.dueDate) ? ((act.assignedAt || act.dueDate) | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">
            <strong>Aún no tienes actividades asignadas.</strong>
            <span>Cuando te unas a una actividad o programa institucional se mostrará aquí.</span>
          </div>
        }
      }

      <!-- PESTAÑA 3: TODAS (TABLA COMBINADA - CERO HORAS) -->
      @if (accActiveTab === 'todas') {
        @if (totalRecordsCount > 0) {
          <div class="sd-combined-table-wrapper">
            <table class="sd-combined-table">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Origen</th>
                  <th>Tipo / Título</th>
                  <th>Calificación de Desempeño</th>
                  <th>Estado</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                @for (a of volunteer.accreditations; track (a._id || a.interactionFolio || 'acc') + '-' + $index) {
                  <tr>
                    <td>
                      @if (a.interactionFolio) {
                        <app-folio-pill
                          [folio]="a.interactionFolio"
                          [interactionType]="a.interactionType || 'OP'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td><span class="origin-badge origin-ac">Acreditación</span></td>
                    <td><strong>{{ getAccreditationTypeLabel(a.type) }}</strong></td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(a.status, a.performance)">
                        {{ getPerformanceLabel(a.status, a.performance) }}
                      </span>
                      @if (a.performanceComment || a.comments) {
                        <small class="table-comment-sub">{{ a.performanceComment || a.comments }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="a.status === 'approved' || a.status === 'accredited' ? 'perf-good' : (a.status === 'rejected' ? 'perf-bad' : 'perf-neutral')"
                      >
                        {{ getAccreditationStatusLabel(a.status) }}
                      </span>
                    </td>
                    <td>{{ a.date ? (a.date | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }
                @for (ac of volunteer.assignedActivities; track (ac.activityRef || ac.interactionFolio || 'act') + '-' + $index) {
                  <tr>
                    <td>
                      @if (ac.interactionFolio) {
                        <app-folio-pill
                          [folio]="ac.interactionFolio"
                          [interactionType]="ac.interactionType || 'AC'"
                          [showHours]="false"
                        ></app-folio-pill>
                      } @else {
                        —
                      }
                    </td>
                    <td><span class="origin-badge origin-act">Actividad</span></td>
                    <td><strong>{{ ac.title || ac.type || 'Actividad asignada' }}</strong></td>
                    <td>
                      <span class="performance-pill" [ngClass]="getPerformanceClass(ac.status, ac.performance)">
                        {{ getPerformanceLabel(ac.status, ac.performance) }}
                      </span>
                      @if (ac.performanceComment) {
                        <small class="table-comment-sub">{{ ac.performanceComment }}</small>
                      }
                    </td>
                    <td>
                      <span
                        class="performance-pill"
                        [ngClass]="ac.status === 'validated' ? 'perf-good' : (ac.status === 'absent' ? 'perf-absent' : 'perf-neutral')"
                      >
                        {{ getActivityStatusLabel(ac.status) }}
                      </span>
                    </td>
                    <td>{{ (ac.assignedAt || ac.dueDate) ? ((ac.assignedAt || ac.dueDate) | date:'dd/MM/yyyy') : '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <div class="empty-state">
            <strong>No hay registros de participación en tu expediente.</strong>
          </div>
        }
      }
    </section>

    <!-- =========================================================
         11. MIS FOLIOS DE PARTICIPACIÓN (CERO HORAS)
         ========================================================= -->
    @if (hasAnyFolios) {
      <section class="profile-card folios-participation-card">
        <div class="section-header-row">
          <div>
            <span class="section-eyebrow">TRAZABILIDAD INSTITUCIONAL</span>
            <h2>Mis folios de participación</h2>
            <p>Folios oficiales asignados a tus interacciones dentro de los programas y actividades de ABAQ.</p>
          </div>
        </div>

        <div class="folios-grid">
          @if (volunteer.folio) {
            <div class="folio-item-box">
              <div>
                <small class="folio-item-kicker">
                  EXPEDIENTE GENERAL
                </small>
                <strong class="folio-item-title">Registro institucional</strong>
              </div>
              <app-folio-pill
                [folio]="volunteer.folio"
                interactionType="OP"
                [showHours]="false"
              ></app-folio-pill>
            </div>
          }

          @for (act of volunteer.assignedActivities; track (act.activityRef || act.interactionFolio || 'f-act') + '-' + $index) {
            @if (act.interactionFolio) {
              <div class="folio-item-box">
                <div>
                  <small class="folio-item-kicker">
                    ACTIVIDAD ASIGNADA
                  </small>
                  <strong class="folio-item-title">{{ act.title || act.type || 'Actividad asignada' }}</strong>
                </div>
                <app-folio-pill
                  [folio]="act.interactionFolio"
                  [interactionType]="act.interactionType || 'AC'"
                  [showHours]="false"
                ></app-folio-pill>
              </div>
            }
          }

          @for (acc of volunteer.accreditations; track (acc._id || acc.interactionFolio || 'f-acc') + '-' + $index) {
            @if (acc.interactionFolio) {
              <div class="folio-item-box">
                <div>
                  <small class="folio-item-kicker">
                    ACREDITACIÓN DE COORDINADOR
                  </small>
                  <strong class="folio-item-title">{{ getAccreditationTypeLabel(acc.type) }}</strong>
                </div>
                <app-folio-pill
                  [folio]="acc.interactionFolio"
                  [interactionType]="acc.interactionType || 'OP'"
                  [showHours]="false"
                ></app-folio-pill>
              </div>
            }
          }
        </div>
      </section>
    }

  </div>
} @else {
  <div class="profile-error">
    No fue posible cargar la información de tu perfil. Por favor intenta más tarde.
  </div>
}
